import { gql } from '@apollo/client';

/**
 * OTRIX Play — GraphQL documents (exercises catalog, weekly routine, self-reported
 * ranking, and the shared-routines forum). Consumed by the play components via
 * useQuery / useMutation.
 */

export const PLAY_EXERCISE_FIELDS = gql`
  fragment PlayExerciseFields on Exercise {
    id
    name
    localized_name
    localized_description
    localized_instructions
    muscle_group
    secondary_muscles
    location
    exercise_type
    suggested_sets
    suggested_reps
    image_url_1
    image_url_2
    benchmark_metric
    benchmark_target
    parameter_types
  }
`;

/* ----------------------------- Exercises catalog ---------------------------- */

export const PLAY_EXERCISES = gql`
  query playExercises($muscle_group: MuscleGroup, $location: ExerciseLocation) {
    playExercises(muscle_group: $muscle_group, location: $location) {
      ...PlayExerciseFields
    }
  }
  ${PLAY_EXERCISE_FIELDS}
`;

/* ------------------------------ Weekly routine ------------------------------ */

export const MY_WEEKLY_ROUTINE = gql`
  query myWeeklyRoutine {
    myWeeklyRoutine {
      id
      name
      source_shared_routine_id
      progression_week
      days {
        id
        day_of_week
        intensity
        is_manual
      }
      items {
        id
        exercise_id
        day_of_week
        position
        target_sets
        target_reps
        rest_seconds
        notes
        exercise {
          ...PlayExerciseFields
        }
      }
    }
  }
  ${PLAY_EXERCISE_FIELDS}
`;

// Fija a mano la intensidad de un día (menú "···" de la tarjeta del día).
export const SET_ROUTINE_DAY_INTENSITY = gql`
  mutation setRoutineDayIntensity($day_of_week: Int!, $intensity: RoutineIntensity!) {
    setRoutineDayIntensity(day_of_week: $day_of_week, intensity: $intensity) {
      id
      day_of_week
      intensity
      is_manual
    }
  }
`;

export const ADD_ROUTINE_ITEM = gql`
  mutation addRoutineItem(
    $exercise_id: ID!
    $day_of_week: Int!
    $target_sets: Int
    $target_reps: String
    $notes: String
  ) {
    addRoutineItem(
      input: {
        exercise_id: $exercise_id
        day_of_week: $day_of_week
        target_sets: $target_sets
        target_reps: $target_reps
        notes: $notes
      }
    ) {
      id
      day_of_week
      position
    }
  }
`;

export const UPDATE_ROUTINE_ITEM = gql`
  mutation updateRoutineItem($id: ID!, $target_sets: Int, $target_reps: String, $notes: String, $position: Int) {
    updateRoutineItem(
      id: $id
      input: { target_sets: $target_sets, target_reps: $target_reps, notes: $notes, position: $position }
    ) {
      id
    }
  }
`;

export const MOVE_ROUTINE_ITEM = gql`
  mutation moveRoutineItem($id: ID!, $day_of_week: Int!, $position: Int) {
    moveRoutineItem(id: $id, day_of_week: $day_of_week, position: $position) {
      id
      day_of_week
    }
  }
`;

export const REMOVE_ROUTINE_ITEM = gql`
  mutation removeRoutineItem($id: ID!) {
    removeRoutineItem(id: $id)
  }
`;

export const CLEAR_ROUTINE_DAY = gql`
  mutation clearRoutineDay($day_of_week: Int!) {
    clearRoutineDay(day_of_week: $day_of_week) {
      id
    }
  }
`;

/* --------------------- Workout engine (Iniciar rutina) ---------------------- */

// Resumen de entrenamientos completados: alimenta la card "Rutina de hoy" (Sesión N/M) y
// la card de Rendimientos (sesiones + tiempo total + racha).
export const MY_PLAY_WORKOUT_SUMMARY = gql`
  query myPlayWorkoutSummary {
    myPlayWorkoutSummary {
      completed_this_week
      completed_total
      total_seconds
      last_workout_at
      current_streak_days
    }
  }
`;

// Historial de entrenamientos (Rendimientos).
export const MY_PLAY_WORKOUTS = gql`
  query myPlayWorkouts($first: Int) {
    myPlayWorkouts(first: $first) {
      id
      day_of_week
      muscle_focus
      started_at
      completed_at
      total_seconds
      work_seconds
      rest_seconds
      exercise_count
      total_sets
      estimated_calories
      completion_percent
      status
      created_at
      # Series/reps por ejercicio: alimenta "Entrenamientos con este ejercicio" en Rendimientos.
      details
    }
  }
`;

// Guarda un entrenamiento terminado (el cronómetro corre en el front).
export const LOG_PLAY_WORKOUT = gql`
  mutation logPlayWorkout($input: LogPlayWorkoutInput!) {
    logPlayWorkout(input: $input) {
      id
      total_seconds
      completion_percent
      status
      completed_at
    }
  }
`;

/* --------------------- Play profile / onboarding wizard --------------------- */

export const MY_PLAY_PROFILE = gql`
  query myPlayProfile {
    myPlayProfile {
      id
      goal
      location_pref
      height_cm
      weight_kg
      days_per_week
      experience_level
      target_muscles
      preferred_exercise_types
      onboarding_completed_at
    }
  }
`;

export const SAVE_PLAY_PROFILE = gql`
  mutation savePlayProfile(
    $goal: String
    $location_pref: ExerciseLocation
    $height_cm: Float
    $weight_kg: Float
    $age: Int
    $days_per_week: Int
    $experience_level: String
    $target_muscles: [MuscleGroup!]
    $preferred_exercise_types: [String!]
    $complete: Boolean
  ) {
    savePlayProfile(
      input: {
        goal: $goal
        location_pref: $location_pref
        height_cm: $height_cm
        weight_kg: $weight_kg
        age: $age
        days_per_week: $days_per_week
        experience_level: $experience_level
        target_muscles: $target_muscles
        preferred_exercise_types: $preferred_exercise_types
        complete: $complete
      }
    ) {
      id
      goal
      location_pref
      onboarding_completed_at
    }
  }
`;

export const GENERATE_WEEKLY_ROUTINE = gql`
  mutation generateWeeklyRoutine($regenerate: Boolean) {
    generateWeeklyRoutine(regenerate: $regenerate) {
      id
      name
      items {
        id
        day_of_week
        position
        target_sets
        target_reps
      }
    }
  }
`;

/* -------------------------- Self-reported ranking --------------------------- */

export const PLAY_RANKING = gql`
  query playRanking($exercise_id: ID!) {
    playRanking(exercise_id: $exercise_id) {
      rank
      user_id
      user_name
      user_avatar
      value
      metric
      photo_url
      is_flagged
    }
  }
`;

export const MY_PLAY_RESULTS = gql`
  query myPlayResults($exercise_id: ID) {
    myPlayResults(exercise_id: $exercise_id) {
      id
      value
      metric
      photo_url
      source
      is_flagged
      review_status
      rating
      feedback
      rated_at
      created_at
      rater {
        id
        full_name
      }
      exercise {
        id
        localized_name
        muscle_group
        image_url_1
        image_url_2
      }
    }
  }
`;

// Entrenador/admin: todos los resultados auto-reportados de un cliente (para calificar).
export const CLIENT_PLAY_RESULTS = gql`
  query clientPlayResults($user_id: ID!) {
    clientPlayResults(user_id: $user_id) {
      id
      value
      metric
      photo_url
      is_flagged
      review_status
      rating
      feedback
      rated_at
      created_at
      rater {
        id
        full_name
      }
      exercise {
        id
        localized_name
        muscle_group
      }
    }
  }
`;

/**
 * Listado GLOBAL de marcas Play para el panel de Rendimientos del admin/entrenador. Los
 * clientes de Play no generan `dynamicPerformances` (eso lo registra un entrenador), así que
 * sin esta query no aparecían en ninguna parte del panel.
 */
export const PLAY_SELF_REPORTED_RESULTS = gql`
  query playSelfReportedResults($client_id: ID, $exercise_id: ID, $first: Int) {
    playSelfReportedResults(client_id: $client_id, exercise_id: $exercise_id, first: $first) {
      id
      value
      metric
      photo_url
      source
      is_flagged
      review_status
      rating
      created_at
      user {
        id
        full_name
        photo
      }
      exercise {
        id
        localized_name
        muscle_group
      }
    }
  }
`;

/* -------------------- Muscle-group ranking (C11 · Play only) ----------------- */

export const PLAY_MUSCLE_DISTRIBUTION = gql`
  query playMuscleDistribution {
    playMuscleDistribution {
      muscle_group
      count
      percent
    }
  }
`;

export const MY_MUSCLE_DISTRIBUTION = gql`
  query myMuscleDistribution {
    myMuscleDistribution {
      muscle_group
      count
      percent
    }
  }
`;

export const PLAY_MUSCLE_RANKING = gql`
  query playMuscleRanking($muscle_group: MuscleGroup!) {
    playMuscleRanking(muscle_group: $muscle_group) {
      rank
      user_id
      user_name
      user_avatar
      count
      best_value
    }
  }
`;

// Dashboard summary — my global Play position with the neighbour above and below.
export const MY_PLAY_RANKING_SUMMARY = gql`
  query myPlayRankingSummary {
    myPlayRankingSummary {
      my_rank
      my_points
      total_athletes
      above {
        rank
        user_name
        user_avatar
        points
        is_me
      }
      me {
        rank
        user_name
        user_avatar
        points
        is_me
      }
      below {
        rank
        user_name
        user_avatar
        points
        is_me
      }
    }
  }
`;

export const GRADE_SELF_REPORTED_RESULT = gql`
  mutation gradeSelfReportedResult($id: ID!, $rating: Int!, $feedback: String) {
    gradeSelfReportedResult(id: $id, rating: $rating, feedback: $feedback) {
      id
      rating
      feedback
      rated_at
    }
  }
`;

export const DELETE_SELF_REPORTED_RESULT = gql`
  mutation deleteSelfReportedResult($id: ID!) {
    deleteSelfReportedResult(id: $id)
  }
`;

export const SUBMIT_SELF_REPORTED_RESULT = gql`
  mutation submitSelfReportedResult($exercise_id: ID!, $value: Float!, $metric: BenchmarkMetric!, $photo: Upload!) {
    submitSelfReportedResult(exercise_id: $exercise_id, value: $value, metric: $metric, photo: $photo) {
      id
      value
      metric
      is_flagged
      review_status
    }
  }
`;

/* ------------------------------ Shared routines ----------------------------- */

export const SHARED_ROUTINES = gql`
  query sharedRoutines($muscle_focus: String) {
    sharedRoutines(muscle_focus: $muscle_focus) {
      id
      author_name
      name
      description
      muscle_focus
      adopt_count
      like_count
      comment_count
      is_liked
      created_at
      user {
        id
        full_name
        photo
        is_online
        # Badge "Coach" en el feed: la entrenadora/admin publica con distintivo.
        role
      }
      items {
        id
        day_of_week
        position
        target_sets
        target_reps
        exercise {
          id
          localized_name
          muscle_group
        }
      }
    }
  }
`;

// `name` va NULLABLE a propósito: el composer del foro ya no pide título y el backend lo deriva
// de la descripción (SharedRoutineMutation@resolveName). Con String! el envío de null reventaba
// la validación de variables y la publicación fallaba.
export const PUBLISH_ROUTINE = gql`
  mutation publishRoutine($name: String, $description: String, $muscle_focus: String, $attach_routine: Boolean) {
    publishRoutine(name: $name, description: $description, muscle_focus: $muscle_focus, attach_routine: $attach_routine) {
      id
      name
    }
  }
`;

export const UPDATE_SHARED_ROUTINE = gql`
  mutation updateSharedRoutine($id: ID!, $name: String, $description: String, $muscle_focus: String) {
    updateSharedRoutine(id: $id, name: $name, description: $description, muscle_focus: $muscle_focus) {
      id
      name
      description
      muscle_focus
    }
  }
`;

/* ------------------------------ Play social (follow) ------------------------ */

export const MY_FOLLOWERS_COUNT = gql`
  query myFollowersCount {
    myFollowersCount
  }
`;

export const SUGGESTED_ATHLETES = gql`
  query suggestedAthletes {
    suggestedAthletes {
      id
      full_name
      photo
      routines_count
      followers_count
      is_followed
      is_me
      is_online
    }
  }
`;

// Quiénes dieron like a una rutina del foro (modal al tocar el contador del corazón).
export const ROUTINE_LIKERS = gql`
  query routineLikers($shared_routine_id: ID!) {
    routineLikers(shared_routine_id: $shared_routine_id) {
      id
      full_name
      photo
      routines_count
      followers_count
      is_followed
      is_me
      is_online
    }
  }
`;

export const PLAY_ATHLETE = gql`
  query playAthlete($user_id: ID!) {
    playAthlete(user_id: $user_id) {
      id
      full_name
      photo
      member_since
      followers_count
      following_count
      routines_count
      is_followed
      is_me
      is_online
      routines {
        id
        name
        description
        muscle_focus
        adopt_count
        like_count
        comment_count
        created_at
      }
    }
  }
`;

export const FOLLOW_USER = gql`
  mutation followUser($user_id: ID!) {
    followUser(user_id: $user_id)
  }
`;

export const UNFOLLOW_USER = gql`
  mutation unfollowUser($user_id: ID!) {
    unfollowUser(user_id: $user_id)
  }
`;

export const ADOPT_ROUTINE = gql`
  mutation adoptRoutine($id: ID!) {
    adoptRoutine(id: $id) {
      id
    }
  }
`;

export const REPORT_ROUTINE = gql`
  mutation reportRoutine($id: ID!, $reason: String) {
    reportRoutine(id: $id, reason: $reason)
  }
`;

export const DELETE_SHARED_ROUTINE = gql`
  mutation deleteSharedRoutine($id: ID!) {
    deleteSharedRoutine(id: $id)
  }
`;

/* ------------------------ Forum social (likes + comments) ------------------- */

export const TOGGLE_ROUTINE_LIKE = gql`
  mutation toggleRoutineLike($id: ID!) {
    toggleRoutineLike(id: $id) {
      id
      like_count
      is_liked
    }
  }
`;

export const ROUTINE_COMMENTS = gql`
  query routineComments($shared_routine_id: ID!) {
    routineComments(shared_routine_id: $shared_routine_id) {
      id
      body
      author_name
      user_id
      like_count
      is_liked
      created_at
      user {
        id
        full_name
        photo
      }
      replies {
        id
        body
        author_name
        user_id
        like_count
        is_liked
        created_at
        user {
          id
          full_name
          photo
        }
      }
    }
  }
`;

export const TOGGLE_COMMENT_LIKE = gql`
  mutation toggleCommentLike($id: ID!) {
    toggleCommentLike(id: $id) {
      id
      like_count
      is_liked
    }
  }
`;

export const ADD_ROUTINE_COMMENT = gql`
  mutation addRoutineComment($shared_routine_id: ID!, $parent_id: ID, $body: String!) {
    addRoutineComment(shared_routine_id: $shared_routine_id, parent_id: $parent_id, body: $body) {
      id
      body
    }
  }
`;

export const DELETE_ROUTINE_COMMENT = gql`
  mutation deleteRoutineComment($id: ID!) {
    deleteRoutineComment(id: $id)
  }
`;

/* --------------------------- Exercise frames (admin) ------------------------ */

// Upload a single frame into slot 1 or 2 (two separate images → stop-motion).
export const UPLOAD_EXERCISE_FRAME = gql`
  mutation uploadExerciseFrame($id: ID!, $slot: Int!, $image: Upload!) {
    uploadExerciseFrame(id: $id, slot: $slot, image: $image) {
      id
      image_url_1
      image_url_2
    }
  }
`;

export const REMOVE_EXERCISE_FRAME = gql`
  mutation removeExerciseFrame($id: ID!, $slot: Int!) {
    removeExerciseFrame(id: $id, slot: $slot) {
      id
      image_url_1
      image_url_2
    }
  }
`;

/* ------------------------------ Moderation (admin) -------------------------- */

// Self-reported results flagged by sanity caps, awaiting review.
export const FLAGGED_PLAY_RESULTS = gql`
  query flaggedPlayResults {
    flaggedPlayResults {
      id
      value
      metric
      photo_url
      review_status
      created_at
      exercise {
        id
        localized_name
      }
      user {
        id
        full_name
      }
    }
  }
`;

export const REVIEW_SELF_REPORTED_RESULT = gql`
  mutation reviewSelfReportedResult($id: ID!, $status: String!) {
    reviewSelfReportedResult(id: $id, status: $status) {
      id
      review_status
    }
  }
`;

// Shared routines with at least one report, for moderation.
export const REPORTED_ROUTINES = gql`
  query reportedRoutines {
    reportedRoutines {
      id
      author_name
      name
      description
      muscle_focus
      moderation_status
      report_count
      adopt_count
      items {
        id
        exercise {
          id
          localized_name
        }
      }
    }
  }
`;

export const MODERATE_ROUTINE = gql`
  mutation moderateRoutine($id: ID!, $status: String!) {
    moderateRoutine(id: $id, status: $status) {
      id
      moderation_status
    }
  }
`;
